/**
 * Statement tables for P5. Each statement is a tree of line specs; `values` reads every line (leaf and total) of one period into
 * a record, and `buildRows` lays the current period, the prior quarter and the change side by side. A period that is not there
 * (the first quarter's prior) is "—", never 0.
 */
import type { BookId } from "@bsim/domain";
import { formatMetric } from "@gxs/metric-defs";
import { roundedToSum } from "../format";
import type { TableRow } from "./DataTable";
import { balanceTotals, type Statements } from "./balance";

export interface LineSpec {
  readonly id: string;
  readonly label: string;
  readonly total?: boolean;
  readonly children?: readonly LineSpec[];
}

type Values = Readonly<Record<string, number>>;

export const BOOK_LABELS: Readonly<Record<string, string>> = {
  cash: "Cash and reserves", ust_afs: "US Treasuries (available for sale)", mbs_afs: "Mortgage bonds (available for sale)", mbs_htm: "Mortgage bonds (held to maturity)",
  ci: "Business loans", cre: "Commercial property loans", resi: "Residential mortgages", consumer: "Consumer loans", card: "Card loans",
  dda: "Non-interest checking", int_checking: "Interest checking", mmda: "Money market deposits", time: "Certificates of deposit", brokered: "Brokered certificates of deposit",
  fhlb: "Federal Home Loan Bank advances", ffp: "Fed funds purchased", dw: "Discount window", sub_debt: "Subordinated debt",
};
const STATIC_LABELS: Readonly<Record<string, string>> = {
  premises: "Premises", goodwill: "Goodwill", intangibles_cdi: "Core deposit intangibles", boli: "Bank-owned life insurance", dta: "Deferred tax asset", other_assets: "Other assets",
};
export const FEE_LABELS: Readonly<Record<string, string>> = { deposit: "Deposit fees", treasury: "Treasury fees", card: "Card fees", wealth: "Wealth fees", mortgage: "Mortgage fees", other: "Other fees", ecr: "Earnings credit paid on business checking", "lapsing.ecr": "Earnings credit paid under a card, until it ends" };
/** The engine's own posting keys for operating expense (`opex.<key>` accounts, `lineAttribution` ids): the table, its causes and the engine's attribution read one set of ids. */
export const OPEX_LABELS: Readonly<Record<string, string>> = {
  comp: "Compensation", hiring: "Hiring and training", occupancy: "Occupancy and branches", technology: "Technology", marketing: "Marketing", cdi: "Intangibles amortization", workout: "Workout costs on problem loans",
  events: "Event costs and losses", branchOpening: "New branch costs", other: "Other expense", depositInsurance: "Deposit insurance",
};
/** The one-time opex lines (07.3 H, E6, E7; 10.1 E5a adds them back to the price's earnings): shown as three lines under one subtotal. */
export const ONE_OFF_OPEX_LABELS: Readonly<Record<string, string>> = { "oneOff.restructuring": "Restructuring charge", "oneOff.leaseExit": "Branch lease exits", "oneOff.event": "Event one-time costs" };

const leaves = (ids: readonly string[], prefix: string, labels: Readonly<Record<string, string>>): LineSpec[] => ids.map((id) => ({ id: `${prefix}${id}`, label: labels[id] ?? id }));

export const BALANCE_SPEC: readonly LineSpec[] = [
  { id: "assets", label: "Assets", total: true, children: [
    { id: "cash", label: BOOK_LABELS["cash"] ?? "Cash" },
    { id: "securities", label: "Securities", children: leaves(["ust_afs", "mbs_afs", "mbs_htm"], "", BOOK_LABELS) },
    { id: "loans", label: "Loans, net of the allowance", children: [...leaves(["ci", "cre", "resi", "consumer", "card"], "", BOOK_LABELS), { id: "acl", label: "Less: allowance for credit losses" }] },
    { id: "otherAssets", label: "Other assets", children: leaves(["premises", "goodwill", "intangibles_cdi", "boli", "dta", "other_assets"], "", STATIC_LABELS) },
  ] },
  { id: "liabilities", label: "Liabilities", total: true, children: [
    { id: "deposits", label: "Deposits", children: leaves(["dda", "int_checking", "mmda", "time", "brokered"], "", BOOK_LABELS) },
    { id: "borrowings", label: "Borrowings", children: leaves(["fhlb", "ffp", "dw", "sub_debt"], "", BOOK_LABELS) },
    { id: "other_liabilities", label: "Other liabilities" },
  ] },
  { id: "equity", label: "Equity", total: true, children: [
    { id: "commonApic", label: "Common stock and paid-in capital" }, { id: "retainedEarnings", label: "Retained earnings" },
    { id: "aociAfs", label: "Bond losses counted in equity" }, { id: "aociSwap", label: "Swap marks in equity" },
  ] },
  { id: "liabilitiesAndEquity", label: "Liabilities and equity", total: true },
];

export function balanceValues(s: Statements): Values {
  const b = s.balanceSheet;
  const book = (id: string) => b.booksUsd[id as BookId] ?? 0;
  const stat = (id: string) => b.staticItemsUsd[id as keyof typeof b.staticItemsUsd] ?? 0;
  const t = balanceTotals(s);
  const out: Record<string, number> = { acl: -b.aclUsd, other_liabilities: stat("other_liabilities"), assets: t.assetsUsd, liabilities: t.liabilitiesUsd, equity: t.equityUsd, liabilitiesAndEquity: t.liabilitiesUsd + t.equityUsd };
  for (const id of ["cash", "ust_afs", "mbs_afs", "mbs_htm", "ci", "cre", "resi", "consumer", "card", "dda", "int_checking", "mmda", "time", "brokered", "fhlb", "ffp", "dw", "sub_debt"]) out[id] = book(id);
  for (const id of ["premises", "goodwill", "intangibles_cdi", "boli", "dta", "other_assets"]) out[id] = stat(id);
  out["securities"] = book("ust_afs") + book("mbs_afs") + book("mbs_htm");
  out["loans"] = ["ci", "cre", "resi", "consumer", "card"].reduce((n, id) => n + book(id), 0) - b.aclUsd;
  out["otherAssets"] = ["premises", "goodwill", "intangibles_cdi", "boli", "dta", "other_assets"].reduce((n, id) => n + stat(id), 0) + Math.max(book("swap_overlay"), 0);
  out["deposits"] = ["dda", "int_checking", "mmda", "time", "brokered"].reduce((n, id) => n + book(id), 0);
  out["borrowings"] = ["fhlb", "ffp", "dw", "sub_debt"].reduce((n, id) => n + book(id), 0);
  Object.assign(out, { commonApic: b.equity.commonApicUsd, retainedEarnings: b.equity.retainedEarningsUsd, aociAfs: b.equity.aociAfsUsd, aociSwap: b.equity.aociSwapUsd });
  return out;
}

export const INCOME_SPEC: readonly LineSpec[] = [
  { id: "interestIncome", label: "Interest income", children: leaves(["cash", "ust_afs", "mbs_afs", "mbs_htm", "ci", "cre", "resi", "consumer", "card"], "ii.", BOOK_LABELS) },
  { id: "interestExpense", label: "Interest expense", children: leaves(["int_checking", "mmda", "time", "brokered", "fhlb", "ffp", "dw", "sub_debt"], "ie.", BOOK_LABELS) },
  { id: "swapNet", label: "Swap carry" },
  { id: "nii", label: "Net interest income", total: true },
  { id: "fees", label: "Fee income", children: leaves(["deposit", "treasury", "ecr", "lapsing.ecr", "card", "wealth", "mortgage", "other"], "fee.", FEE_LABELS) },
  { id: "opex", label: "Operating expense", children: [...leaves(Object.keys(OPEX_LABELS), "opex.", OPEX_LABELS), { id: "opex.unlisted", label: "Other operating costs" }, { id: "opex.oneOff", label: "One-time costs", children: leaves(Object.keys(ONE_OFF_OPEX_LABELS), "opex.", ONE_OFF_OPEX_LABELS) }] },
  { id: "provision", label: "Provision for credit losses" },
  { id: "securitiesGains", label: "Gains and losses on sales (bonds and loans)" },
  { id: "pretax", label: "Pre-tax income", total: true },
  { id: "tax", label: "Income tax" },
  { id: "netIncome", label: "Net income", total: true },
];

export function incomeValues(s: Statements): Values {
  const i = s.incomeStatement;
  const out: Record<string, number> = {};
  const add = (prefix: string, rec: Readonly<Partial<Record<string, number>>>, sign: 1 | -1) => {
    let total = 0;
    for (const id of Object.keys(rec)) { const v = sign * (rec[id] ?? 0); out[`${prefix}${id}`] = v; total += v; }
    return total;
  };
  out["interestIncome"] = add("ii.", i.interestIncomeUsd, 1);
  out["interestExpense"] = add("ie.", i.interestExpenseUsd, -1);
  out["fees"] = add("fee.", i.feeIncomeUsd, 1);
  out["opex"] = add("opex.", i.opexUsd, -1);
  out["opex.oneOff"] = Object.keys(ONE_OFF_OPEX_LABELS).reduce((n, id) => n + (out[`opex.${id}`] ?? 0), 0);
  // Any posting key the table has no label for lands in one computed line, so the children always add to the parent.
  out["opex.unlisted"] = out["opex"] - Object.keys(OPEX_LABELS).reduce((n, id) => n + (out[`opex.${id}`] ?? 0), 0) - out["opex.oneOff"];
  Object.assign(out, { swapNet: i.swapNetUsd, nii: out["interestIncome"] + out["interestExpense"] + i.swapNetUsd, provision: -i.provisionUsd, securitiesGains: i.securitiesGainsUsd, pretax: i.pretaxUsd, tax: -i.taxUsd, netIncome: i.netIncomeUsd });
  return out;
}

const MILLION = 1e6;

/** The lines a total adds up: its children, or (where its parts are siblings: a net income of pre-tax income and tax) the ids named here. */
const PARTS: Readonly<Record<string, readonly string[]>> = {
  liabilitiesAndEquity: ["liabilities", "equity"],
  nii: ["interestIncome", "interestExpense", "swapNet"],
  pretax: ["nii", "fees", "opex", "provision", "securitiesGains"],
  netIncome: ["pretax", "tax"],
};

/**
 * Every line of one period in whole $ millions, rounded so the printed lines add: a total is rounded on its own, and its parts are rounded to the total with the rounding left over
 * given to the largest part (`roundedToSum`). A total the parts do not add to within rounding (the engine's own gap) keeps its parts as they round. PROPOSAL: whole millions, one rule for every statement.
 */
function printedMillions(spec: readonly LineSpec[], values: Values): Record<string, number> {
  const out: Record<string, number> = {};
  const byId = new Map<string, LineSpec>();
  const walk = (lines: readonly LineSpec[]) => lines.forEach((l) => { byId.set(l.id, l); walk(l.children ?? []); });
  walk(spec);
  const partsOf = (id: string): readonly string[] => PARTS[id] ?? (byId.get(id)?.children ?? []).map((c) => c.id);
  const assign = (id: string, printed: number) => {
    out[id] = printed;
    const parts = partsOf(id).filter((p) => values[p] !== undefined);
    if (parts.length === 0) return;
    const trues = parts.map((p) => values[p] ?? 0);
    const sum = trues.reduce((t, v) => t + v, 0);
    const exact = Math.abs(sum - (values[id] ?? 0)) < (parts.length * MILLION) / 2 + 1;
    const printedParts = exact ? roundedToSum(trues, printed * MILLION, MILLION).map((v) => Math.round(v / MILLION)) : trues.map((v) => Math.round(v / MILLION));
    parts.forEach((p, i) => assign(p, printedParts[i] ?? 0));
  };
  const asPart = new Set([...byId.keys()].flatMap((id) => partsOf(id)));
  for (const id of byId.keys()) if (!asPart.has(id) && values[id] !== undefined) assign(id, Math.round((values[id] ?? 0) / MILLION));
  return out;
}

/** "28,122", the true minus, "—" for zero; `signed` for the change column. */
const millions = (n: number, signed = false): string => (n === 0 ? "—" : formatMetric(n, "index", { precision: 0, signed }));

/** One row per line: this period, then (only where a prior period exists) the prior quarter and the change, then (only where the twin's statement is held) your twin's figure. A column with nothing behind it is not drawn. Figures are whole $ millions (the header says so); the change is the difference of the printed figures. */
function toRows(spec: readonly LineSpec[], cur: Record<string, number>, prior: Record<string, number> | null, twin: Record<string, number> | null): TableRow[] {
  return spec.map((line) => {
    const now = cur[line.id];
    const before = prior?.[line.id];
    const theirs = twin?.[line.id];
    const row: TableRow = {
      id: line.id, label: line.label,
      cells: [
        now === undefined ? null : millions(now),
        ...(prior === null ? [] : [before === undefined ? null : millions(before), now === undefined || before === undefined ? null : millions(now - before, true)]),
        ...(twin === null ? [] : [theirs === undefined ? null : millions(theirs)]),
      ],
      ...(line.total === true ? { total: true, open: true } : {}),
      ...(line.children === undefined ? {} : { children: toRows(line.children, cur, prior, twin) }),
    };
    return row;
  });
}

/** This period, the prior quarter and the change when there is a prior period, and your twin's figure when the twin's statement is held; whole $ millions that add. */
export const buildRows = (spec: readonly LineSpec[], cur: Values, prior: Values | null, twin: Values | null = null): TableRow[] =>
  toRows(spec, printedMillions(spec, cur), prior === null ? null : printedMillions(spec, prior), twin === null ? null : printedMillions(spec, twin));
